// ─────────────────────────────────────────────
//  testSession — a SHARED testing environment several people can be in at once.
//
//  Owner, 2026-09-07: *"if you are in a testing mode and open 2 different
//  browsers, to check and interact with multiple users you need to see the
//  changes… when you create the testing environment all the other testers can
//  interact with each other."*
//
//  ⚠️ THIS IS NOT `domain/testMode`, AND THE DIFFERENCE IS THE WHOLE POINT.
//  Testing mode (`atc.testMode`) snapshots a day into an IndexedDB store inside
//  ONE browser profile. It is per-device by construction: there is no sync
//  channel, so two browsers hold two unrelated copies and neither can see the
//  other. That is correct for "let me poke at a past day by myself" and cannot
//  be stretched to cover several people rehearsing together.
//
//  A shared session therefore needs a shared PLANE. Rather than invent one, this
//  reuses the isolation primitive the entire app already runs on: the facility.
//  A session IS a facility — `tst-<code>` — seeded by copying a real facility's
//  day into it. Every listener, rule, alert and push then works unchanged,
//  because they were all facility-scoped already.
//
//  ⚠️ AND THAT IS EXACTLY WHY THIS FILE EXISTS SEPARATELY FROM facilities.js.
//  Making a test session a real facility buys the whole stack for free, and
//  hands us one sharp edge in exchange: `activeFacilityIds()` feeds
//  `functions/dailyImport`, which FULL-RESETS every board it is given. A test
//  facility reachable from that list would be rewritten nightly — and worse, a
//  bug that let a real facility id through here would point a rehearsal at a
//  live OR board. So the id shape is checked, not trusted, and the predicate is
//  written to fail toward "this is a test facility, keep it out of the real
//  paths" rather than toward "looks real, let it through".
// ─────────────────────────────────────────────

// The prefix is load-bearing, not cosmetic: it is what lets any layer — client,
// rules, a Cloud Function, a human reading the console — tell a rehearsal from
// a hospital without a lookup. Kept short and lowercase because it becomes part
// of a document id people type to join.
export const TEST_FACILITY_PREFIX = "tst-";

// Six characters from an unambiguous alphabet: no 0/O, no 1/l/I. A person reads
// this code off one screen and types it into another, and a join code that
// fails because somebody typed O for 0 teaches them the feature is broken.
const CODE_ALPHABET = "23456789abcdefghjkmnpqrstuvwxyz";
export const TEST_CODE_LENGTH = 6;

/**
 * Is this id a shared test session?
 *
 * ⚠️ SHAPE-CHECKED, NEVER TRUSTED. Called on ids that arrive from Firestore
 * docs, from a URL, and from a human typing a join code, so a bare
 * `startsWith` would answer true for `tst-` alone or for `tst-../../default`.
 * Anything that is not exactly the prefix plus a full code is NOT a session —
 * and, because the callers use this to keep things OUT of destructive paths,
 * an unrecognised value must never be treated as a test facility either.
 */
export function isTestFacilityId(id) {
  const s = String(id || "");
  if (!s.startsWith(TEST_FACILITY_PREFIX)) return false;
  const code = s.slice(TEST_FACILITY_PREFIX.length);
  if (code.length !== TEST_CODE_LENGTH) return false;
  return [...code].every((ch) => CODE_ALPHABET.includes(ch));
}

/**
 * Is this facility DOC a shared test session?
 *
 * ⚠️ EITHER SIGNAL IS ENOUGH, ON PURPOSE. The id shape and the `isTest` flag
 * are written together, so disagreement means something is wrong — and the
 * safe reading of "something is wrong" is "treat it as a test", because the
 * cost of that mistake is a rehearsal excluded from the nightly import, while
 * the cost of the opposite is a nightly board reset pointed at real staffing.
 * Fail toward the harmless error.
 */
export function isTestFacility(doc) {
  if (!doc) return false;
  return doc.isTest === true || isTestFacilityId(doc.id);
}

/**
 * A new session code. `random` is injectable so tests are deterministic and so
 * this stays pure — the house rule for everything in domain/.
 */
export function newTestFacilityId(random = Math.random) {
  let code = "";
  for (let i = 0; i < TEST_CODE_LENGTH; i += 1) {
    const n = Math.floor(random() * CODE_ALPHABET.length);
    // A random() of exactly 1 would index past the end; clamp rather than
    // retry, so this always terminates.
    code += CODE_ALPHABET[Math.min(n, CODE_ALPHABET.length - 1)];
  }
  return `${TEST_FACILITY_PREFIX}${code}`;
}

// What a human types to join. Case- and space-insensitive, and it accepts the
// full id as readily as the bare code, because people will copy whichever one
// is in front of them.
export function normalizeJoinCode(input) {
  const s = String(input || "").trim().toLowerCase().replace(/\s+/g, "");
  if (!s) return null;
  const id = s.startsWith(TEST_FACILITY_PREFIX) ? s : `${TEST_FACILITY_PREFIX}${s}`;
  return isTestFacilityId(id) ? id : null;
}

// How long a session may live before the sweeper is entitled to delete it.
// A rehearsal is a sitting, not a fixture; anything still standing the next day
// is litter in a production project.
export const TEST_SESSION_TTL_HOURS = 12;

/**
 * The facility document for a session.
 *
 * ⚠️ `facilityStatus: "inactive"` IS DELIBERATE AND IS THE SECOND LOCK.
 * `isFacilityActive` already refuses a test facility outright, but that
 * predicate is client-side. Writing the doc inactive as well means that even a
 * layer which never heard of test sessions — a future Cloud Function, another
 * tenant's tooling, a hand-run script — reaches the same conclusion through the
 * lifecycle rule it already respects. Two independent reasons to exclude it,
 * so removing one does not open the destructive path.
 */
export function buildTestFacilityDoc({
  id,
  sourceFacilityId,
  sourceFacilityLabel = "",
  date,
  createdBy,
  createdAt,
  expiresAt,
}) {
  return {
    id,
    isTest: true,
    facilityStatus: "inactive",
    isActive: false,
    // Named so nobody has to decode the id to know what they are looking at,
    // in the picker or in the Firestore console.
    name: `TEST · ${sourceFacilityLabel || sourceFacilityId} · ${date}`,
    label: `TEST · ${sourceFacilityLabel || sourceFacilityId} · ${date}`,
    shortTag: "TEST",
    sourceFacilityId,
    sourceDate: date,
    createdBy,
    createdAt,
    expiresAt,
  };
}

// The collections a session copies. Same list and same day-scoping as the
// single-browser snapshot, so the two ways of testing a day disagree about
// nothing. `staff` and `users` are NOT copied: they are tenant-wide and already
// readable, and duplicating auth records into a rehearsal is how a test session
// would start carrying push tokens.
export const TEST_SESSION_COLLECTIONS = [
  { collection: "rooms", dateField: null },
  { collection: "assignments", dateField: "date" },
  { collection: "daily_boards", dateField: "date" },
  { collection: "or_cases", dateField: "date" },
  { collection: "breaks", dateField: "date" },
];

// ⚠️ `alerts` AND `tasks` ARE DELIBERATELY NOT COPIED, AND THIS IS A SAFETY
// DECISION, NOT AN OMISSION.
//
// Both collections carry unconditional onCreate push triggers (functions/
// pushSend.js, functions/taskPush.js) that deliver to `targetIds` /
// `recipientIds`. This seeder deliberately does NOT remap staff and user ids —
// that is what keeps real names and roles on a rehearsal board — so those
// fields on a seeded copy are REAL clinicians' auth uids.
//
// Copying them would therefore have re-fired a facility's alert history as live
// push: "🚨 EMERGENCY", high priority, on the channel built to sound through a
// silenced phone, to people asleep and not on call. `alerts` had no date bound
// either, so it was the WHOLE history, not one day.
//
// The triggers now also ignore any document carrying `isTestData: true`, so
// there are two independent defences; removing one does not reopen it. A
// rehearsal that needs alerts should raise its own inside the session.
export const TEST_SESSION_UNCOPIED = ["alerts", "tasks"];
